import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import toast from 'react-hot-toast';

const STAGE_ORDER = [['applied', 'Applied', 'bg-slate-400'], ['screening', 'Screening', 'bg-sky-500'], ['interview', 'Interview', 'bg-amber-500'], ['offer', 'Offer', 'bg-violet-500'], ['hired', 'Hired', 'bg-emerald-500'], ['rejected', 'Rejected', 'bg-rose-400']];

function Stat({ icon, label, value, sub, color }) {
  return (
    <div className="bg-surface rounded-2xl border border-border p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 text-text-muted text-xs font-semibold uppercase"><i className={`fa-solid ${icon} ${color}`} />{label}</div>
      <p className="text-2xl font-bold text-text-primary mt-2">{value}</p>
      {sub && <p className="text-[11px] text-text-muted mt-0.5">{sub}</p>}
    </div>
  );
}

export default function HiringAnalytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get('/api/hiring/analytics').then(r => setA(r.data)).catch(() => toast.error('Failed to load analytics')); }, []);
  if (!a) return <div className="py-16 text-center text-text-muted text-sm"><i className="fa-solid fa-circle-notch fa-spin text-2xl block mb-3" />Loading analytics…</div>;

  const maxStage = Math.max(1, ...Object.values(a.by_stage));

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon="fa-users" label="Applicants" value={a.total_applicants} color="text-primary" />
        <Stat icon="fa-calendar-check" label="Interviews" value={a.interviews} color="text-amber-500" />
        <Stat icon="fa-user-check" label="Hires" value={a.hires} color="text-emerald-500" />
        <Stat icon="fa-stopwatch" label="Time to Hire" value={a.avg_time_to_hire_days != null ? `${a.avg_time_to_hire_days}d` : '—'} sub="avg days applied → hired" color="text-sky-500" />
        <Stat icon="fa-briefcase" label="Open Jobs" value={a.open_jobs} sub={`${a.total_jobs} total`} color="text-violet-500" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Funnel */}
        <div className="bg-surface rounded-2xl border border-border p-6">
          <p className="text-sm font-bold text-text-primary mb-4"><i className="fa-solid fa-filter text-primary mr-2" />Pipeline Funnel</p>
          <div className="space-y-3">
            {STAGE_ORDER.map(([k, l, c]) => (
              <div key={k}>
                <div className="flex justify-between text-xs text-text-secondary mb-1"><span>{l}</span><span className="font-bold">{a.by_stage[k] || 0}</span></div>
                <div className="h-2.5 rounded-full bg-surface-alt overflow-hidden"><div className={`h-full ${c}`} style={{ width: `${(a.by_stage[k] || 0) / maxStage * 100}%` }} /></div>
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-center">
            <div className="bg-surface-alt rounded-xl p-3"><p className="text-lg font-bold text-primary">{a.conversion.applied_to_interview}%</p><p className="text-[10px] text-text-muted uppercase">Applied → Interview</p></div>
            <div className="bg-surface-alt rounded-xl p-3"><p className="text-lg font-bold text-emerald-600">{a.conversion.interview_to_hire}%</p><p className="text-[10px] text-text-muted uppercase">Interview → Hire</p></div>
          </div>
        </div>

        {/* By source */}
        <div className="bg-surface rounded-2xl border border-border p-6">
          <p className="text-sm font-bold text-text-primary mb-4"><i className="fa-solid fa-signal text-primary mr-2" />Recruiting Performance by Source</p>
          <table className="w-full text-xs">
            <thead><tr className="text-text-muted uppercase text-[10px] text-left"><th className="pb-2">Source</th><th className="pb-2 text-right">Applicants</th><th className="pb-2 text-right">Hires</th><th className="pb-2 text-right">Hire rate</th></tr></thead>
            <tbody className="divide-y divide-border">
              {a.by_source.map((s, i) => (
                <tr key={i} data-testid="source-row"><td className="py-2 text-text-secondary truncate max-w-[140px]">{s.source}</td><td className="py-2 text-right">{s.applicants}</td><td className="py-2 text-right">{s.hires}</td><td className="py-2 text-right font-bold text-emerald-600">{s.hire_rate}%</td></tr>
              ))}
              {a.by_source.length === 0 && <tr><td colSpan="4" className="py-4 text-center text-text-muted">No data yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* By job-posting platform */}
      <div className="bg-surface rounded-2xl border border-border p-6">
        <p className="text-sm font-bold text-text-primary mb-1"><i className="fa-solid fa-tower-broadcast text-primary mr-2" />Performance by Job Posting Platform</p>
        <p className="text-[11px] text-text-muted mb-4">Tracked via the per-platform apply links on each job (Jobs & Posting → Copy tracked link).</p>
        <table className="w-full text-xs">
          <thead><tr className="text-text-muted uppercase text-[10px] text-left"><th className="pb-2">Platform</th><th className="pb-2 text-right">Applicants</th><th className="pb-2 text-right">Interviews</th><th className="pb-2 text-right">Hires</th><th className="pb-2 text-right">Hire rate</th><th className="pb-2 text-right">Avg time-to-hire</th></tr></thead>
          <tbody className="divide-y divide-border">
            {(a.by_platform || []).map((p, i) => (
              <tr key={i} data-testid="platform-row">
                <td className="py-2 text-text-secondary font-semibold">{p.label}</td>
                <td className="py-2 text-right">{p.applicants}</td>
                <td className="py-2 text-right">{p.interviews}</td>
                <td className="py-2 text-right">{p.hires}</td>
                <td className="py-2 text-right font-bold text-emerald-600">{p.hire_rate}%</td>
                <td className="py-2 text-right">{p.avg_time_to_hire_days != null ? `${p.avg_time_to_hire_days}d` : '—'}</td>
              </tr>
            ))}
            {(!a.by_platform || a.by_platform.length === 0) && <tr><td colSpan="6" className="py-4 text-center text-text-muted">No applications yet</td></tr>}
          </tbody>
        </table>
      </div>

      {/* By recruiter */}
      <div className="bg-surface rounded-2xl border border-border p-6">
        <p className="text-sm font-bold text-text-primary mb-4"><i className="fa-solid fa-user-tie text-primary mr-2" />Recruiter / Manager Ownership</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {a.by_recruiter.map((r, i) => (
            <div key={i} className="bg-surface-alt rounded-xl p-4" data-testid="recruiter-card">
              <p className="text-sm font-semibold text-text-primary truncate">{r.recruiter}</p>
              <p className="text-xs text-text-muted mt-1">{r.candidates} owned · <span className="text-emerald-600 font-bold">{r.hires} hired</span></p>
            </div>
          ))}
          {a.by_recruiter.length === 0 && <p className="text-xs text-text-muted col-span-full">No candidates assigned yet.</p>}
        </div>
      </div>
    </div>
  );
}
